
import { useAuth } from "@/hooks/use-auth";
import { useAdminStatus } from "@/hooks/use-admin-status";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useTokenOperations() {
  const { user, isAuthenticated } = useAuth();
  const { isAdmin } = useAdminStatus();

  const useToken = async (tokensToUse: number = 1): Promise<boolean> => {
    if (!isAuthenticated || !user) {
      toast.error("Vous devez être connecté pour utiliser cette fonctionnalité");
      return false;
    }

    if (isAdmin) {
      console.log("Admin user detected - bypassing token usage");
      return true;
    }

    try {
      const { data, error } = await supabase.rpc('decrement_user_tokens', {
        user_id_param: user.id,
        tokens_to_use: tokensToUse
      });

      if (error) {
        console.error('Error using token:', error);
        toast.error("Erreur lors de l'utilisation du token");
        return false;
      }

      if (!data) {
        toast.error("🎁 Votre token est insuffisant. Veuillez faire un don via Ko-fi pour obtenir plus de tokens.");
        return false;
      }

      console.log("Token usage successful");
      return true;
    } catch (error) {
      console.error('Error using token:', error);
      toast.error("Erreur lors de l'utilisation du token");
      return false;
    }
  };

  const claimDailyTokens = async (currentTokens: number, canClaim: boolean): Promise<{ success: boolean; newTokens?: number }> => {
    if (!isAuthenticated || !user) {
      toast.error("Vous devez être connecté pour réclamer vos tokens gratuits");
      return { success: false };
    }

    if (!canClaim) {
      toast.error("Vous avez déjà réclamé vos tokens gratuits. Revenez dans 8 heures !");
      return { success: false };
    }

    try {
      console.log("Claiming daily tokens...");
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          tokens: currentTokens + 3,
          last_daily_claim: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error claiming daily tokens:', error);
        toast.error("Erreur lors de la réclamation des tokens gratuits");
        return { success: false };
      }

      if (data) {
        await supabase
          .from('token_transactions')
          .insert({
            user_id: user.id,
            email: user.email || '',
            transaction_type: 'donation',
            amount: 3,
            description: 'Tokens gratuits quotidiens'
          });

        toast.success("🎉 Vous avez reçu 3 tokens gratuits !");
        console.log("New token balance:", data.tokens);
        return { success: true, newTokens: data.tokens };
      }
    } catch (error) {
      console.error('Error claiming daily tokens:', error);
      toast.error("Erreur lors de la réclamation des tokens gratuits");
      return { success: false };
    }

    return { success: false };
  };

  return {
    useToken,
    claimDailyTokens
  };
}
